/**
 * Hook that wires live data sources into the fill probability math module.
 *
 * Subscribes to the trade event bus for a self-contained 30m sliding window
 * (scoped to NBA moneyline tickers with active orders only).
 * Recomputes fill probabilities on a configurable interval (default 30s).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { KalshiOrder } from '../../lib/kalshiApi';
import type { KalshiOrderbook } from '../../types';
import {
  isNbaMoneylineTicker,
  determinePriceLevel,
  computeFillProbability,
  extractEventTicker,
  type FillProbResult,
} from '../../lib/trading/fillProbability';
import { getHoursToTipoff } from '../../lib/market/eventStartTimeStore';
import { onTradeEvent, type MarketTradeEvent } from '../../lib/trading/tradeEventBus';

interface TradeRecord {
  count: number;
  timestamp: number;
}

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

export interface UseFillProbabilityParams {
  orders: KalshiOrder[];
  absoluteQueueByOrderId: Record<string, number>;
  obCache: Map<string, KalshiOrderbook>;
  refreshIntervalMs?: number;
}

/**
 * Returns a map of order_id → FillProbResult | null for each order.
 * Non-NBA, missing inputs, or out-of-range hours produce null.
 */
export function useFillProbability({
  orders,
  absoluteQueueByOrderId,
  obCache,
  refreshIntervalMs = 30_000,
}: UseFillProbabilityParams): Record<string, FillProbResult | null> {
  const [results, setResults] = useState<Record<string, FillProbResult | null>>({});

  // 30m sliding window trade buffer per ticker
  const tradeBufferRef = useRef(new Map<string, TradeRecord[]>());

  // Keep orders ref current for trade event filter
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  // Subscribe to trade events (NBA moneyline only)
  useEffect(() => {
    const unsub = onTradeEvent((event: MarketTradeEvent) => {
      if (event.type !== 'market_trade') return;
      if (!isNbaMoneylineTicker(event.ticker)) return;

      const buffer = tradeBufferRef.current;
      const records = buffer.get(event.ticker) ?? [];
      records.push({ count: event.count, timestamp: event.timestamp });
      buffer.set(event.ticker, records);
    });

    return unsub;
  }, []);

  // Compute fill probabilities on interval
  const compute = useCallback(() => {
    const now = Date.now();
    const buffer = tradeBufferRef.current;
    const cutoff = now - THIRTY_MINUTES_MS;

    // Prune stale trade records
    for (const [ticker, records] of buffer) {
      const pruned = records.filter((r) => r.timestamp >= cutoff);
      if (pruned.length === 0) {
        buffer.delete(ticker);
      } else {
        buffer.set(ticker, pruned);
      }
    }

    const next: Record<string, FillProbResult | null> = {};

    for (const order of ordersRef.current) {
      if (!isNbaMoneylineTicker(order.ticker)) {
        next[order.order_id] = null;
        continue;
      }

      // Extract event ticker and compute hours to tipoff
      const eventTicker = extractEventTicker(order.ticker);
      const hoursToTipoff = getHoursToTipoff(eventTicker);
      if (hoursToTipoff == null) {
        next[order.order_id] = null;
        continue;
      }

      // Contracts ahead from absolute queue depth
      const contractsAhead = absoluteQueueByOrderId[order.order_id] ?? 0;

      // Fill velocity: sum trade counts in 30m window for this ticker
      const trades = buffer.get(order.ticker) ?? [];
      const fillVel30m = trades.reduce((sum, r) => sum + r.count, 0);

      // Determine price level (L1/L2) from orderbook
      const orderPrice = order.side === 'yes' ? order.yes_price : order.no_price;
      if (orderPrice == null) {
        next[order.order_id] = null;
        continue;
      }

      const ob = obCache.get(order.ticker);
      const bestBid = getBestBidForSide(ob, order.side);
      const level = determinePriceLevel(orderPrice, bestBid);
      if (!level) {
        next[order.order_id] = null;
        continue;
      }

      next[order.order_id] = computeFillProbability(level, {
        hoursToTipoff,
        contractsAhead,
        fillVel30m,
      });
    }

    setResults(next);
  }, [absoluteQueueByOrderId, obCache]);

  useEffect(() => {
    compute(); // run immediately
    const id = setInterval(compute, refreshIntervalMs);
    return () => clearInterval(id);
  }, [compute, refreshIntervalMs]);

  return results;
}

/**
 * Extract best bid price for a given side from orderbook.
 * YES bids: highest price in yes array. NO bids: highest price in no array.
 */
export function getBestBidForSide(
  ob: KalshiOrderbook | undefined,
  side: 'yes' | 'no'
): number | null {
  if (!ob) return null;
  const levels = side === 'yes' ? ob.yes : ob.no;
  if (!levels || levels.length === 0) return null;

  let best = -1;
  for (const [price] of levels) {
    if (price > best) best = price;
  }
  return best === -1 ? null : best;
}
